import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DepartmentService } from '@app/reducers/orm/department/department.service';
import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import { EmployeeService } from '@app/reducers/orm/employee/employee.service';
import { DepartmentOptionsPerLocation, LocationModel } from '@app/reducers/orm/location/location.model';
import { LocationService } from '@app/reducers/orm/location/location.service';
import { ShiftTemplatePayload } from '@app/reducers/orm/shift/shift.model';
import { ShiftService } from '@app/reducers/orm/shift/shift.service';
import { TeamModel } from '@app/reducers/orm/team/team.model';
import { TeamService } from '@app/reducers/orm/team/team.service';
import { ToastService } from '@sb/ui/toast/toast.service';
import { lastValueFrom } from 'rxjs';

import { DevToolsDataGenerator } from './dev-tools-data-generator';
import { DevToolsRateLimiter } from './dev-tools-rate-limiter';

export interface ScaffoldConfig {
  locationsToCreate: number;
  departmentsToCreate: number;
  teamsToCreate: number;
  employeesToCreate: number;
  existingLocations: LocationModel[];
  existingDepartments: DepartmentOptionsPerLocation[];
  existingTeams: TeamModel[];
}

@Injectable()
export class DevToolsScaffoldService {
  private locationService = inject(LocationService);
  private departmentService = inject(DepartmentService);
  private teamService = inject(TeamService);
  private employeeService = inject(EmployeeService);
  private shiftService = inject(ShiftService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  #rateLimiter = inject(DevToolsRateLimiter);

  private locationsCreated = 0;
  private departmentsCreated = 0;
  private teamsCreated = 0;
  private employeesCreated = 0;

  async scaffold(config: ScaffoldConfig, onProgress: (message: string) => void): Promise<void> {
    this.locationsCreated = 0;
    this.departmentsCreated = 0;
    this.teamsCreated = 0;
    this.employeesCreated = 0;

    const locationIds = await this.handleLocations(config, onProgress);
    const departmentIds = await this.handleDepartments(config, locationIds, onProgress);
    const teamIds = await this.handleTeams(config, departmentIds, onProgress);
    await this.handleEmployees(config, teamIds, onProgress);

    this.showScaffoldingSuccessMessage();
  }

  /**
   * Handles location creation. Returns IDs of all available locations (existing + newly created).
   */
  private async handleLocations(config: ScaffoldConfig, onProgress: (message: string) => void): Promise<string[]> {
    const existingLocationIds = config.existingLocations.map((loc) => loc.id).filter(Boolean);

    if (config.locationsToCreate === 0) {
      return existingLocationIds;
    }

    onProgress(`Creating ${config.locationsToCreate} location(s)...`);

    const locations = Array.from({ length: config.locationsToCreate }, () => DevToolsDataGenerator.generateLocation());

    // Create all locations in parallel — the rate limiter spaces out the requests
    const createPromises = locations.map(async (location) => {
      await this.#rateLimiter.waitForSlot('locations/');

      return this.toPromise<any>(this.locationService.save(location))
        .then((response: any) => {
          const locationId =
            response?.result ||
            response?.entities?.locations?.[Object.keys(response?.entities?.locations || {})[0]]?.id;
          return locationId || null;
        })
        .catch((err) => {
          console.error('Failed to create location:', err);
          return null; // Continue even if one location fails
        });
    });

    const createdLocationIds = (await Promise.all(createPromises)).filter(Boolean) as string[];

    if (createdLocationIds.length === 0) {
      this.toastService.openDanger('No locations created');
      throw new Error('No locations created');
    }

    this.locationsCreated = createdLocationIds.length;
    return [...existingLocationIds, ...createdLocationIds];
  }

  /**
   * Handles department creation. Returns IDs of all available departments (existing + newly created).
   */
  private async handleDepartments(
    config: ScaffoldConfig,
    locationIds: string[],
    onProgress: (message: string) => void,
  ): Promise<string[]> {
    const existingDepartmentIds = config.existingDepartments
      .flatMap((dept) => dept.departments)
      .map((dept) => dept.id)
      .filter(Boolean);

    if (config.departmentsToCreate === 0) {
      return existingDepartmentIds;
    }

    onProgress('Creating departments...');

    // Distribute departments evenly across locations
    const departmentsPerLocation = Math.floor(config.departmentsToCreate / locationIds.length);
    const remainder = config.departmentsToCreate % locationIds.length;
    const allDepartments: ReturnType<typeof DevToolsDataGenerator.generateDepartments> = [];

    locationIds.forEach((locationId, index) => {
      const count = departmentsPerLocation + (index < remainder ? 1 : 0);
      const departments = DevToolsDataGenerator.generateDepartments(locationId, count);
      allDepartments.push(...departments);
    });

    try {
      await this.#rateLimiter.waitForSlot('departments/batch');

      const response = await this.toPromise<any>(this.departmentService.batchUpdate(allDepartments));
      const createdDepts = response?.entities?.departments || {};
      const createdDeptIds = Object.keys(createdDepts);

      if (createdDeptIds.length === 0) {
        this.toastService.openDanger('Failed to create departments');
        throw new Error('Failed to create departments');
      } else {
        // Create a shift template for every new department as a lot of logic currently relies on the existence of at least one
        const shiftTemplates: ShiftTemplatePayload[] = createdDeptIds.map((deptId) =>
          DevToolsDataGenerator.generateShiftTemplate(deptId),
        );
        await this.#rateLimiter.waitForSlot('shifts/batch');
        await this.toPromise<any>(this.shiftService.batch(shiftTemplates));
      }

      this.departmentsCreated = createdDeptIds.length;
      return [...existingDepartmentIds, ...createdDeptIds];
    } catch (err: any) {
      console.error('Failed to create departments:', err);
      this.toastService.openDanger(err?.message || 'Failed to create departments');
      throw err;
    }
  }

  /**
   * Handles team creation. Returns IDs of all available teams (existing + newly created).
   */
  private async handleTeams(
    config: ScaffoldConfig,
    departmentIds: string[],
    onProgress: (message: string) => void,
  ): Promise<string[]> {
    const existingTeamIds = config.existingTeams.map((team) => team.id).filter(Boolean);

    if (config.teamsToCreate === 0) {
      return existingTeamIds;
    }

    onProgress('Creating teams...');

    // Distribute teams evenly across departments
    const teamsPerDepartment = Math.floor(config.teamsToCreate / departmentIds.length);
    const remainder = config.teamsToCreate % departmentIds.length;
    const allTeams: ReturnType<typeof DevToolsDataGenerator.generateTeams> = [];

    departmentIds.forEach((departmentId, index) => {
      const count = teamsPerDepartment + (index < remainder ? 1 : 0);
      const teams = DevToolsDataGenerator.generateTeams(departmentId, count);
      allTeams.push(...teams);
    });

    try {
      await this.#rateLimiter.waitForSlot('teams/batch');

      const response = await this.toPromise<any>(
        this.teamService.batch(allTeams, { headers: { ignoreMetaMessage: true } }),
      );
      const createdTeams = response?.entities?.teams || {};
      const createdTeamIds = Object.keys(createdTeams);

      if (createdTeamIds.length === 0) {
        this.toastService.openDanger('No teams created');
        throw new Error('No teams created');
      }

      this.teamsCreated = createdTeamIds.length;
      return [...existingTeamIds, ...createdTeamIds];
    } catch (err: any) {
      console.error('Failed to create teams:', err);
      this.toastService.openDanger(err?.message || 'Failed to create teams');
      throw err;
    }
  }

  /**
   * Handles employee creation.
   */
  private async handleEmployees(
    config: ScaffoldConfig,
    teamIds: string[],
    onProgress: (message: string) => void,
  ): Promise<void> {
    if (config.employeesToCreate === 0) {
      return;
    }

    if (teamIds.length === 0) {
      this.toastService.openDanger('No teams available to assign employees');
      throw new Error('No teams available');
    }

    onProgress('Creating employees...');

    try {
      // Fetch existing employees to get their emails and prevent conflicts
      const loadResponse = await this.toPromise<any>(this.employeeService.load());

      // Extract existing emails from the response
      const existingEmails: string[] = [];
      if (loadResponse?.entities?.employees) {
        Object.values(loadResponse.entities.employees).forEach((employee: EmployeeModel) => {
          if (employee?.email) {
            existingEmails.push(employee.email);
          }
        });
      }

      // Generate employees with existing emails to prevent conflicts
      const employees = DevToolsDataGenerator.generateEmployees(teamIds, config.employeesToCreate, existingEmails);

      // Save the generated employees
      await this.#rateLimiter.waitForSlot('users/batch');
      await this.toPromise<any>(
        this.employeeService.saveMany({ User: employees }, { headers: { ignoreMetaMessage: true } }),
      );

      this.employeesCreated = config.employeesToCreate;
    } catch (err: any) {
      console.error('Failed to create employees:', err);
      this.toastService.openDanger(err?.message || 'Failed to create employees');
      throw err;
    }
  }

  /**
   * Helper method to convert an Observable to a Promise.
   * Uses takeUntilDestroyed to ensure proper cleanup.
   */
  private toPromise<T>(observable: any): Promise<T> {
    return lastValueFrom(observable.pipe(takeUntilDestroyed(this.destroyRef)));
  }

  private showScaffoldingSuccessMessage(): void {
    const parts: string[] = [];

    if (this.locationsCreated > 0)
      parts.push(`${this.locationsCreated} location${this.locationsCreated !== 1 ? 's' : ''}`);
    if (this.departmentsCreated > 0)
      parts.push(`${this.departmentsCreated} department${this.departmentsCreated !== 1 ? 's' : ''}`);
    if (this.teamsCreated > 0) parts.push(`${this.teamsCreated} team${this.teamsCreated !== 1 ? 's' : ''}`);
    if (this.employeesCreated > 0)
      parts.push(`${this.employeesCreated} employee${this.employeesCreated !== 1 ? 's' : ''}`);

    if (parts.length === 0) {
      this.toastService.openSuccess('Scaffolding complete');
    } else {
      const message = `Project scaffolded: ${parts.join(', ')} created`;
      this.toastService.openSuccess(message);
    }
  }
}
